import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  CheckCheck,
  ChevronDown,
  FileArchive,
  Hammer,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { schematicDisplayTitle, schematicShowsFilename } from '../../utils/buildSchematic';
import type {
  AssignmentStatus,
  BuildAssignment,
  BuildSchematic,
  BuildTeamMember,
} from '../../types/buildTeam';
import { StatTile } from '../ui/StatTile';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { BuildSchematicsSection } from './BuildSchematicsSection';
import { cn } from '../../utils/cn';

const dateLabel = (value: number) =>
  format(new Date(value < 1e12 ? value * 1000 : value), 'd MMM yyyy');
const targetPath = (type: 'project' | 'build', id: string) =>
  `/build-team/${type === 'project' ? 'projects' : 'builds'}/${encodeURIComponent(id)}`;
const noUpdate = () => {};

export function BuilderContributions({
  member,
  onDownload,
}: {
  member: BuildTeamMember;
  onDownload: (schematic: BuildSchematic) => void;
}) {
  const [assignments, setAssignments] = useState<BuildAssignment[]>([]);
  const [uploads, setUploads] = useState<BuildSchematic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [filter, setFilter] = useState<AssignmentStatus | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [uploadPage, setUploadPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      buildTeamApiService.getTeamMemberAssignments(member.id),
      buildTeamApiService.getSchematicsByUploader(member.minecraft_uuid),
    ])
      .then(([history, files]) => {
        if (!cancelled) {
          setAssignments(history);
          setUploads(files);
        }
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err instanceof Error
              ? err.message
              : 'Could not load contributions.',
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [member.id, member.minecraft_uuid, retry]);

  const projects = new Set(
    assignments
      .map(
        (a) =>
          a.project_id ?? (a.target_type === 'project' ? a.target_id : null),
      )
      .filter(Boolean),
  );
  const filtered = useMemo(
    () =>
      assignments.filter(
        (a) =>
          (!filter || a.status === filter) &&
          `${a.target_title ?? a.target_id} ${a.project_title ?? ''} ${a.role}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [assignments, filter, search],
  );

  if (loading)
    return (
      <div
        className="space-y-3 pt-4"
        aria-label="Loading contributions"
        aria-busy="true"
      >
        <SkeletonLoader className="h-20 w-full" />
        <SkeletonLoader className="h-32 w-full" />
      </div>
    );
  if (error)
    return (
      <div className="alert alert-danger mt-4" role="alert">
        {error}
        <button
          type="button"
          className="glass-button-secondary ml-3"
          onClick={() => setRetry((n) => n + 1)}
        >
          Try again
        </button>
      </div>
    );

  return (
    <div className="space-y-5 pt-5 mt-3 border-t border-glass-200/10">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <StatTile
          label="Projects"
          value={projects.size}
          icon={Briefcase}
          hint="Including assigned builds"
        />
        <StatTile
          label="Active assignments"
          value={assignments.filter((a) => a.status === 'active').length}
          icon={Hammer}
          tone="accent"
          onClick={() => {
            setFilter('active');
            setPage(1);
          }}
          active={filter === 'active'}
        />
        <StatTile
          label="Completed assignments"
          value={assignments.filter((a) => a.status === 'completed').length}
          icon={CheckCheck}
          tone="success"
          onClick={() => {
            setFilter('completed');
            setPage(1);
          }}
          active={filter === 'completed'}
        />
        <StatTile
          label="Uploads"
          value={uploads.length}
          icon={FileArchive}
          hint="Schematics & screenshots"
        />
      </div>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-white">Work history</h4>
            <p className="text-xs text-glass-400 mt-1">
              Assignments recorded in Argus, including previous work.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              aria-label="Search contribution history"
              className="glass-input text-sm w-full sm:w-52"
              placeholder="Find a project or build…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <select
              aria-label="Assignment status"
              className="glass-select text-sm"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as AssignmentStatus | '');
                setPage(1);
              }}
            >
              <option value="">All history</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="removed">Previous assignments</option>
            </select>
          </div>
        </div>
        {filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title={
              assignments.length
                ? 'No matching assignments'
                : 'No assignments recorded'
            }
            description={
              assignments.length
                ? 'Try another search or choose All history.'
                : 'Work appears here when this builder is assigned to a project or build.'
            }
          />
        ) : (
          <div className="divide-y divide-glass-200/10">
            {filtered.slice((page - 1) * 10, page * 10).map((a) => (
              <div key={a.id} className="py-3">
                <div className="flex items-start gap-3">
                  <span className="icon-tile mt-1">
                    {a.target_type === 'project' ? (
                      <Briefcase className="w-4 h-4" />
                    ) : (
                      <Hammer className="w-4 h-4" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    {a.target_title ? (
                      <Link
                        className="text-sm font-medium text-white hover:text-accent-300 break-words"
                        to={targetPath(a.target_type, a.target_id)}
                      >
                        {a.target_title}
                      </Link>
                    ) : (
                      <span className="text-sm text-glass-400">
                        Unavailable {a.target_type}
                      </span>
                    )}
                    <div className="text-xs text-glass-400 mt-1 flex flex-wrap gap-x-2 gap-y-1">
                      <span className="capitalize">{a.role}</span>
                      <span>Assigned {dateLabel(a.assigned_at)}</span>
                      {a.target_type === 'build' &&
                        a.project_id &&
                        a.project_title && (
                          <Link
                            className="text-accent-300 hover:underline"
                            to={targetPath('project', a.project_id)}
                          >
                            {a.project_title}
                          </Link>
                        )}
                    </div>
                    {a.target_status && (
                      <p className="text-xs text-glass-500 mt-1 capitalize">
                        {a.target_type}: {a.target_status.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                  <span
                    className={cn(
                      'badge shrink-0',
                      a.status === 'completed'
                        ? 'badge-success'
                        : a.status === 'active'
                          ? 'badge-accent'
                          : 'badge-neutral',
                    )}
                  >
                    {a.status === 'removed'
                      ? 'Previous'
                      : a.status === 'completed'
                        ? 'Completed'
                        : 'Active'}
                  </span>
                </div>
                {!!a.schematic_count && (
                  <button
                    type="button"
                    aria-expanded={expanded === a.id}
                    className="glass-button-secondary text-xs mt-2"
                    onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  >
                    <FileArchive className="w-3.5 h-3.5" />
                    {a.schematic_count} linked file
                    {a.schematic_count === 1 ? '' : 's'}
                    <ChevronDown
                      className={cn(
                        'w-3.5 h-3.5 transition-transform',
                        expanded === a.id && 'rotate-180',
                      )}
                    />
                  </button>
                )}
                {expanded === a.id && (
                  <div className="mt-3">
                    <BuildSchematicsSection
                      targetId={a.target_id}
                      targetType={a.target_type}
                      canCreate={false}
                      canEdit={false}
                      canDelete={false}
                      onUpdate={noUpdate}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {filtered.length > 10 && (
          <div className="flex items-center justify-end gap-3 text-xs text-glass-400">
            <button
              type="button"
              className="glass-button-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <span>
              {page} / {Math.ceil(filtered.length / 10)}
            </span>
            <button
              type="button"
              className="glass-button-secondary"
              disabled={page * 10 >= filtered.length}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-white">
          Uploaded by {member.display_name}
        </h4>
        {uploads.length === 0 ? (
          <p className="text-sm text-glass-400">
            No schematic or screenshot uploads recorded.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {uploads.slice((uploadPage - 1) * 10, uploadPage * 10).map((s) => (
              <div key={s.id} className="glass-card p-3 space-y-2">
                <button
                  type="button"
                  onClick={() => onDownload(s)}
                  className="text-sm text-accent-300 hover:underline break-all text-left inline-flex items-center gap-2"
                >
                  <FileArchive className="w-4 h-4 shrink-0" />
                  {schematicDisplayTitle(s)}
                </button>
                {schematicShowsFilename(s) && (
                  <p className="font-mono text-xs text-glass-500 break-all">{s.filename}</p>
                )}
                <p className="text-xs text-glass-400">
                  {s.version ? `${s.version} · ` : ''}
                  {dateLabel(s.created_at)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {s.linked_targets?.map((target) =>
                    target.title ? (
                      <Link
                        className="chip hover:text-accent-300"
                        key={`${target.target_type}:${target.target_id}`}
                        to={targetPath(target.target_type, target.target_id)}
                      >
                        {target.title}
                      </Link>
                    ) : (
                      <span
                        className="chip"
                        key={`${target.target_type}:${target.target_id}`}
                      >
                        Unavailable {target.target_type}
                      </span>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {uploads.length > 10 && (
          <div className="flex items-center justify-end gap-3 text-xs text-glass-400">
            <button
              type="button"
              className="glass-button-secondary"
              disabled={uploadPage === 1}
              onClick={() => setUploadPage((p) => p - 1)}
            >
              Previous uploads
            </button>
            <span>
              {uploadPage} / {Math.ceil(uploads.length / 10)}
            </span>
            <button
              type="button"
              className="glass-button-secondary"
              disabled={uploadPage * 10 >= uploads.length}
              onClick={() => setUploadPage((p) => p + 1)}
            >
              Next uploads
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
